// 9. 点呼表（管理者向け・§⑨ v1.9）。①ステージ袖点呼表＝1ページ複数演目の一覧表（A4横・印刷CSS）。
// 開始時刻は open_time + Σ(duration+transition) の自動積み上げ（API側算出）。上手/下手は RecitalCast.side を消費。
// 掛け持ちは「◯つ後に出番」を赤字。②③④（照明/会館/MC）は CSV 出力で Numbers 運用（RC-7）。
import { useEffect, useState } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { getRecitalRollcall } from "../../../utils/recitalApi";
import "./recital.scss";

import { excelText, toCsv } from "./csvUtils";
const RC_BREADCRUMBS = [
  { title: "Home", path: "/" },
  { title: "Recital", path: "/recital/2026/rollcall" },
  { title: "Name List", path: "/recital/2026/rollcall" },
];

// 表示名＝統一ラベル「講師名 クラス名 対象【校】」（API側で組立）。
// 旧 class_label/instructor_name にフォールバック（後方互換）。
const rowLabel = (r) =>
  r.label || (r.instructor_name ? `${r.instructor_name} ${r.class_label || ""}` : r.class_label || "");

// CSV ダウンロード（BOM付き・Numbers/Excel 日本語対応）
const downloadCsv = (filename, matrix) => {
  const csv = toCsv(matrix);
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const PER_PAGE = 10; // 1ページの演目数
const CAST_ROWS = 7; // 上手/下手グリッドの行数（縦に流し込み）
const CAST_COLS = 8; // 既定の列数（A4横いっぱい・多めに枠を用意・足りなければ自動で増える）
const CELL_W = 66; // セル幅(px)

// 出演者の氏名グリッド（枠線はCSS背景で描画＝空セルのDOMを作らず高速。
// 名前は左上から列方向に流し込み・余白は空欄＝人数で崩れない）
function CastGrid({ list }) {
  const len = list?.length || 0;
  // 既定 8列。人数が多い演目は列を自動で足して全員入れる。
  const cols = Math.max(CAST_COLS, Math.ceil(len / CAST_ROWS));
  return (
    <div className="rc-rc-grid" style={{ width: cols * CELL_W }}>
      {(list || []).map((c, i) => (
        <div className="rc-rc-name" key={i}>
          {c.kana || c.name}
        </div>
      ))}
    </div>
  );
}

export default function RecitalRollcallPage() {
  const [data, setData] = useState(null); // {event, rows} | null=読込中
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let alive = true;
    getRecitalRollcall()
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e.message || "読み込みに失敗しました"));
    return () => {
      alive = false;
    };
  }, []);

  const rows = data?.rows || [];

  // CSV出力（出演順・開始時刻・クラス名の3列・全行）。用途別の列はNumbers等で各自追記。
  const csvExport = () => {
    const m = [["出演順", "開始時刻", "クラス名"]];
    // 出演順「1-2」が Excel 等で日付（1月2日）に化けないよう文字列形式で出す
    rows.forEach((r) => m.push([excelText(r.number), r.start_time, rowLabel(r)]));
    downloadCsv(`点呼表_${data.event.year}.csv`, m);
  };

  // PDF出力：各A4ページ要素を画像化→jsPDF(A4横)に1ページずつ貼ってDL。
  const exportPdf = async () => {
    if (exporting) return;
    setExporting(true);
    setError("");
    try {
      const [{ toPng, getFontEmbedCSS }, { jsPDF }] = await Promise.all([
        import("html-to-image"),
        import("jspdf"),
      ]);
      const els = document.querySelectorAll(".rc-rc-sheets .rc-rc-page");
      if (!els.length) return;
      // フォント埋め込みCSSは1回だけ計算して全ページで使い回す（毎回計算すると激遅）
      const fontEmbedCSS = await getFontEmbedCSS(els[0]);
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const margin = 6;
      for (let i = 0; i < els.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const dataUrl = await toPng(els[i], {
          pixelRatio: 1,
          backgroundColor: "#ffffff",
          fontEmbedCSS,
        });
        // eslint-disable-next-line no-await-in-loop
        const img = await new Promise((res, rej) => {
          const im = new Image();
          im.onload = () => res(im);
          im.onerror = rej;
          im.src = dataUrl;
        });
        const ratio = Math.min((pw - margin * 2) / img.width, (ph - margin * 2) / img.height);
        const w = img.width * ratio;
        const h = img.height * ratio;
        if (i > 0) pdf.addPage();
        pdf.addImage(dataUrl, "PNG", (pw - w) / 2, margin, w, h);
      }
      pdf.save(`点呼表_${data.event.year}.pdf`);
    } catch (e) {
      setError(e.message || "PDF出力に失敗しました");
    } finally {
      setExporting(false);
    }
  };

  // 10演目ごとにページ分割（A4横・1ページ10演目）
  const pages = [];
  for (let i = 0; i < rows.length; i += PER_PAGE) {
    pages.push(rows.slice(i, i + PER_PAGE));
  }

  return (
    <div className="rc-page rc-page-wide">
      <div className="rc-roll-screen">
        <Breadcrumbs breadcrumbs={RC_BREADCRUMBS} />
        <div className="rc-bar">
          <h3>点呼表 / タイムテーブル</h3>
          <div className="rc-roll-tools">
            <button className="rc-btn pri" onClick={exportPdf} disabled={!data || exporting}>
              {exporting ? "PDF作成中…" : "点呼表PDFを出力"}
            </button>
            <button className="rc-btn" onClick={csvExport} disabled={!data}>CSV出力</button>
          </div>
        </div>
        {error && <p className="rc-lead" style={{ color: "#ff6b6b" }}>{error}</p>}
        {data && !data.event.open_time && (
          <p className="rc-lead">
            ⚠ 開演時刻が未設定のため開始時刻は空欄です。「基本設定」で開演時刻・転換時間を設定してください。
          </p>
        )}
      </div>

      {/* ステージ袖点呼表（A4横・10演目/ページ・固定枠グリッド・PDF出力対象） */}
      {data && rows.length > 0 && (
        <div className="rc-rc-sheets" data-verify-rollcall-rows={rows.length}>
          {pages.map((pageRows, pi) => (
            <div className="rc-rc-page" key={pi}>
              <div className="rc-rc-head">
                <div className="rc-rc-logo">{data.event.title}</div>
                <div className="rc-rc-pageno">
                  <span className="lbl">【点呼用】</span>
                  <span className="n">
                    {pi + 1}/{pages.length}
                  </span>
                  <span className="u">ページ</span>
                </div>
              </div>
              <table className="rc-rc-tbl">
                <thead>
                  <tr>
                    <th className="c-no">演目</th>
                    <th className="c-gene">
                      前日
                      <br />
                      ゲネ
                    </th>
                    <th className="c-time">
                      当日
                      <br />
                      本番
                    </th>
                    <th className="c-cls">クラス名</th>
                    <th className="c-mode">
                      音先
                      <br />
                      板付
                    </th>
                    <th className="c-cast">上手 出演者</th>
                    <th className="c-cnt">
                      上手
                      <br />
                      人数
                    </th>
                    <th className="c-cast">下手 出演者</th>
                    <th className="c-cnt">
                      下手
                      <br />
                      人数
                    </th>
                    <th className="c-cnt">
                      合計
                      <br />
                      人数
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => (
                    <tr key={r.id} className="rc-rc-dance">
                      <td className="c-no">{r.number}</td>
                      <td className="c-gene" />
                      <td className="c-time">{r.start_time}</td>
                      <td className="c-cls">
                        <div>{[r.instructor_name, r.title, r.target].filter(Boolean).join(" ")}</div>
                        {r.studio_name && <div className="rc-rc-studio">【{r.studio_name}】</div>}
                      </td>
                      <td className="c-mode">{r.otosaki}</td>
                      <td className="c-cast">
                        <CastGrid list={r.kamite} />
                      </td>
                      <td className="c-cnt c-big">{r.kamite_count || ""}</td>
                      <td className="c-cast">
                        <CastGrid list={r.shimote} />
                      </td>
                      <td className="c-cnt c-big">{r.shimote_count || ""}</td>
                      <td className="c-cnt c-big">{r.total_count || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
      {data && rows.length === 0 && (
        <p className="rc-lead" style={{ textAlign: "center", padding: 20 }}>
          演目が登録されていません。
        </p>
      )}
    </div>
  );
}
